import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { PRODUCTS, SABOTAGE_BUNDLES, getProduct, SIZES } from "../mock/mock";

const WALL = "/designs/aop/aop_sabotage_skunk.png";

function BundleCard({ bundle }) {
  const { addItem } = useCart();
  const tee = getProduct(bundle.tee);
  const sock = getProduct(bundle.sock);
  const [size, setSize] = useState("L");
  if (!tee || !sock) return null;

  const regular = (tee.sale || tee.price) + (sock.sale || sock.price);
  const price = regular - bundle.save;

  const add = () => {
    addItem(tee, size);
    addItem(sock, "M");
    toast("Bundle added to bag", {
      description: `${bundle.title} — you saved $${bundle.save}.`,
    });
  };

  return (
    <div
      data-testid={`bundle-card-${bundle.id}`}
      className="group relative rounded-xl overflow-hidden bg-card border border-white/10 hover:border-neon/50 transition-colors flex flex-col"
    >
      <span className="absolute top-3 right-3 z-10 text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded bg-neon text-black">
        Save ${bundle.save}
      </span>
      <div className="grid grid-cols-2 gap-px bg-white/10">
        <img src={tee.image} alt={tee.name} loading="lazy" className="w-full aspect-square object-cover bg-[#0e0e10]" />
        <img src={sock.image} alt={sock.name} loading="lazy" className="w-full aspect-square object-cover bg-[#0e0e10]" />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[11px] uppercase tracking-[0.14em] text-white/45">Sabotage bundle</p>
        <h3 className="mt-1 font-display text-2xl text-white leading-tight">{bundle.title}</h3>
        <p className="mt-1.5 text-sm text-white/55 leading-snug">{bundle.blurb}</p>

        <div className="mt-3 flex items-center gap-2">
          <span className="text-white/40 line-through text-sm">${regular}</span>
          <span className="text-neon font-bold text-lg" data-testid={`bundle-price-${bundle.id}`}>${price}</span>
        </div>

        <div className="mt-4">
          <p className="text-[11px] uppercase tracking-[0.14em] text-white/45 mb-2">Tee size</p>
          <div className="flex flex-wrap gap-2">
            {SIZES.map((s) => (
              <button
                key={s}
                data-testid={`bundle-size-${bundle.id}-${s}`}
                onClick={() => setSize(s)}
                className={`h-9 w-11 rounded-md border text-sm font-semibold transition-colors ${
                  size === s
                    ? "bg-neon text-black border-neon"
                    : "bg-white/5 text-white/75 border-white/15 hover:border-white/40"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={add}
          data-testid={`bundle-add-${bundle.id}`}
          className="mt-5 w-full flex items-center justify-center gap-2 bg-neon text-black font-bold uppercase tracking-[0.14em] text-sm py-3.5 rounded-full hover:brightness-95 transition"
        >
          <Plus size={16} /> Add bundle to bag
        </button>
      </div>
    </div>
  );
}

export default function SabotageLookbook() {
  const crew = PRODUCTS.filter((p) => p.brandId === "sabotage");

  return (
    <main className="pt-16 md:pt-[72px]" data-testid="sabotage-lookbook">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `url(${WALL})`, backgroundSize: "420px", backgroundRepeat: "repeat" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-[#0b0b0c]" aria-hidden />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-28">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.28em] text-neon mb-4">
            Ozark Mountain Trash
          </span>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9] max-w-3xl">
            THE SKUNK CREW
          </h1>
          <p className="mt-5 max-w-xl text-white/70 leading-relaxed">
            The whole Sabotage drop in one place — the ornery mascot, the moonshine still,
            Stone County Local 417, and the back-porch banjo. Distressed graffiti prints,
            made-to-order. Proudly trashy.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#bundles"
              data-testid="lookbook-jump-bundles"
              className="bg-neon text-black font-bold uppercase tracking-[0.14em] text-sm px-8 py-4 rounded-full hover:brightness-95 transition"
            >
              Bundle &amp; save
            </a>
            <Link
              to="/shop?brand=sabotage"
              data-testid="lookbook-shop-all"
              className="border border-white/25 text-white font-bold uppercase tracking-[0.14em] text-sm px-8 py-4 rounded-full hover:border-neon hover:text-neon transition"
            >
              Shop all {crew.length} pieces
            </Link>
          </div>
        </div>
      </section>

      {/* Bundles */}
      <section id="bundles" className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-neon">Set it off</span>
            <h2 className="font-display text-3xl md:text-5xl text-white mt-2">Bundle &amp; Save</h2>
          </div>
          <p className="text-sm text-white/50 max-w-xs">
            Grab a tee with its matching graffiti socks and knock $10 off the pair. Auto-applied at checkout.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {SABOTAGE_BUNDLES.map((b) => (
            <BundleCard key={b.id} bundle={b} />
          ))}
        </div>
      </section>

      {/* The crew */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 pb-24">
        <div className="mb-8">
          <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-neon">The full lineup</span>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-2">The Crew</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="lookbook-crew-grid">
          {crew.map((p) => (
            <ProductCard key={p.slug} product={p} />
          ))}
        </div>
      </section>
    </main>
  );
}
